import { useMediaQuery, useTheme } from '@mui/material';
import { useRef } from 'react';
import LangEnum from '../../../lang/LangEnum';
import { useRetryGlobalSearchEntities } from '../../../services/modules/global-search/globalSearch.mutations';
import { useAppEnvironmentStore } from '../../../stores/AppEnvironment/appEnvironment.store';
import { selectIsInStoreEnvironment } from '../../../stores/AppEnvironment/appEnvironment.store.selectors';
import { useTranslation } from '../../translation/TranslationContext';
import { GlobalSearchItemActions } from '../components/ResultRow/resultRow.types';
import {
  ENTITY_LIST_HAS_TEXTUAL_SEARCH,
  INITIAL_PAGE_SIZE,
  LISTBOX_DOM_ID,
} from '../globalSearch.constants';
import { ResultsFooterMode, SearchableEntityType } from '../globalSearch.types';
import { mergeProductCodeResults } from '../lib/mergeProductCodeResults';
import useStaticCommands from '../staticCommands/hooks/useStaticCommands';
import useAddToOrder from './useAddToOrder';
import useGlobalSearch from './useGlobalSearch';
import useGlobalSearchActiveTab from './useGlobalSearchActiveTab';
import useGlobalSearchEntityWarning from './useGlobalSearchEntityWarning';
import useGlobalSearchItemActivation from './useGlobalSearchItemActivation';
import useGlobalSearchKeyboardNav from './useGlobalSearchKeyboardNav';
import useGlobalSearchPagination from './useGlobalSearchPagination';
import useGlobalSearchProductCode from './useGlobalSearchProductCode';
import useGlobalSearchQueryControls from './useGlobalSearchQueryControls';
import useGlobalSearchRecentSearchSession from './useGlobalSearchRecentSearchSession';
import useGlobalSearchResultNavigation from './useGlobalSearchResultNavigation';
import useGlobalSearchTabs from './useGlobalSearchTabs';
import useGlobalSearchTracking from './useGlobalSearchTracking';
import useGlobalSearchViewState from './useGlobalSearchViewState';
import useGlobalSearchVisibleItems from './useGlobalSearchVisibleItems';
import useRecentSearches from './useRecentSearches';
import useResultAccess from './useResultAccess';
import useSuggestedCatalogs from './useSuggestedCatalogs';

interface UseGlobalSearchModalParams {
  isOpen: boolean;
  onClose: () => void;
}

const resolveFooterMode = (entityType: SearchableEntityType | null): ResultsFooterMode => {
  if (!entityType) {
    return 'none';
  }
  if (entityType === 'product') {
    return 'load-more';
  }
  return ENTITY_LIST_HAS_TEXTUAL_SEARCH[entityType] ? 'nav' : 'none';
};

interface IsFanOutSignalActiveParams {
  isSignalActive: boolean;
  hasProductCodeResults: boolean;
}

const isFanOutSignalActive = ({
  isSignalActive,
  hasProductCodeResults,
}: IsFanOutSignalActiveParams): boolean => isSignalActive && !hasProductCodeResults;

const useGlobalSearchModal = ({ isOpen, onClose }: UseGlobalSearchModalParams) => {
  const theme = useTheme();
  const { translate } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isStore = useAppEnvironmentStore(selectIsInStoreEnvironment);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { canOpenEntity, canUseItemAction } = useResultAccess();
  const { recentSearches, addRecentSearch, removeRecentSearch } = useRecentSearches();
  const { catalogs: suggestedCatalogs } = useSuggestedCatalogs({ isEnabled: isOpen });
  const { hasSelectedOrder, isProductInActiveOrder, openProductForView, addProductToOrder } =
    useAddToOrder();

  const {
    query,
    setQuery,
    debouncedQuery,
    isDebouncing,
    hasReachedMinLength,
    data,
    isLoading,
    isFetching,
    isError,
  } = useGlobalSearch({
    enabled: isOpen,
    limit: INITIAL_PAGE_SIZE,
  });

  const { retryEntities, isRetrying } = useRetryGlobalSearchEntities({
    q: debouncedQuery,
    limit: INITIAL_PAGE_SIZE,
  });

  const { items: productCodeItems, isFetching: isFetchingProductCode } = useGlobalSearchProductCode(
    {
      isEnabled: isOpen && hasReachedMinLength,
      query: debouncedQuery,
    },
  );
  const hasProductCodeResults = productCodeItems.length > 0;

  const groups = mergeProductCodeResults(data?.groups ?? [], productCodeItems);
  const warnings = data?.warnings ?? [];

  const hasData = Boolean(data);

  const staticCommands = useStaticCommands({
    query: debouncedQuery,
    isStore,
    onClose,
  });

  const { view, isSearchLoading, showDimmedResults, commands } = useGlobalSearchViewState({
    hasReachedMinLength,
    isError: isFanOutSignalActive({ isSignalActive: isError, hasProductCodeResults }),
    isLoading: isFanOutSignalActive({ isSignalActive: isLoading, hasProductCodeResults }),
    isFetching,
    hasData,
    groups,
    staticCommands,
  });

  const isSearchSettled =
    !isDebouncing && !isFetching && !isFetchingProductCode && view !== 'loading';

  useGlobalSearchTracking({
    isOpen,
    view,
    debouncedQuery,
    hasReachedMinLength,
    suggestedCatalogsCount: suggestedCatalogs.length,
  });

  const { recordRecentSearch } = useGlobalSearchRecentSearchSession({
    isOpen,
    view,
    debouncedQuery,
    isFetching,
    hasProductCodeResults,
    addRecentSearch,
  });

  const { tabs, orderedGroups, allItems, defaultTabId } = useGlobalSearchTabs({
    groups,
    isStore,
    commands,
  });

  const {
    activeTabId,
    setActiveTabId,
    activeCommandType,
    activeEntityType,
    activeGroup,
    isAllTab,
    activeEntityLabel,
  } = useGlobalSearchActiveTab({ tabs, orderedGroups, defaultTabId, isStore, translate, isOpen });

  const { entityWarning } = useGlobalSearchEntityWarning({
    activeEntityType,
    warnings,
    isStore,
    translate,
    onRetry: retryEntities,
  });

  const pagination = useGlobalSearchPagination({
    entityType: activeEntityType,
    query: debouncedQuery,
    overviewGroup: activeGroup,
    isEnabled: view === 'results' && activeEntityType !== null,
  });

  const { visibleItems } = useGlobalSearchVisibleItems({
    isAllTab,
    activeCommandType,
    allItems,
    paginationItems: pagination.items,
    commands,
  });

  const {
    handleSelectItem,
    handleAddProductToOrder,
    handleNavigateToList,
    handleSelectSuggestedCatalog,
  } = useGlobalSearchResultNavigation({
    isStore,
    debouncedQuery,
    activeEntityType,
    canOpenEntity,
    canUseItemAction,
    hasSelectedOrder,
    recordRecentSearch,
    openProductForView,
    addProductToOrder,
    onClose,
  });

  const { handleSelectRecent, handleClearQuery } = useGlobalSearchQueryControls({
    isOpen,
    query,
    inputRef,
    setQuery,
    setActiveTabId,
    onQueryCleared: recordRecentSearch,
  });

  const itemActions: GlobalSearchItemActions | undefined = canUseItemAction('product')
    ? {
        isProductInActiveOrder,
        onAddProductToOrder: handleAddProductToOrder,
      }
    : undefined;

  const { handleActivateItemAction } = useGlobalSearchItemActivation({
    onAddProductToOrder: handleAddProductToOrder,
  });

  const { activeIndex, activeDescendantId, isKeyboardNavActive, handleKeyDown, handleMouseMove } =
    useGlobalSearchKeyboardNav({
      activeTabId,
      resetKey: debouncedQuery,
      query,
      isSearchSettled,
      items: view === 'results' ? visibleItems : [],
      canOpenEntity,
      onSelectItem: handleSelectItem,
      onActivateItemAction: handleActivateItemAction,
    });

  const footerMode = resolveFooterMode(activeEntityType);

  const searchAriaLabel = translate(LangEnum.GLOBAL_SEARCH);

  return {
    view,
    onKeyDown: handleKeyDown,
    search: {
      value: query,
      onChange: setQuery,
      onClear: handleClearQuery,
      isLoading: isSearchLoading,
      placeholder: LangEnum.GLOBAL_SEARCH,
      inputRef,
      combobox: {
        listboxId: LISTBOX_DOM_ID,
        activeDescendantId,
        isResultsOpen: view === 'results',
      },
    },
    recents: {
      searches: recentSearches,
      onSelect: handleSelectRecent,
      onRemove: removeRecentSearch,
    },
    suggestedCatalogs: {
      catalogs: suggestedCatalogs,
      onSelect: handleSelectSuggestedCatalog,
    },
    results: {
      isMobile,
      ariaLabel: searchAriaLabel,
      query,
      tabs,
      activeTabId,
      onSelectTab: setActiveTabId,
      items: visibleItems,
      activeIndex,
      isKeyboardNavActive,
      onMouseMove: handleMouseMove,
      isDimmed: showDimmedResults,
      scrollRef,
      itemActions,
      onSelectItem: handleSelectItem,
      canOpenEntity,
      footerMode,
      footerTerm: debouncedQuery,
      activeEntityLabel,
      hasMore: pagination.hasMore,
      isLoadingMore: pagination.isLoadingMore,
      onLoadMore: pagination.loadMore,
      onNavigateToList: handleNavigateToList,
    },
    warning: entityWarning ? { ...entityWarning, isRetrying } : null,
  };
};

export default useGlobalSearchModal;
